import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useUi, useBlocks } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import { messages } from "./Installation.messages";
import styles from "./Installation.module.scss";

import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import * as appConfig from "appConfig";
import { BioModal } from "@src/lms/jelly/extensions/layouts/_lesson/case4/components/BioModal";

interface InstallationProps {
  title: string;
}

const ACCORDION_IDS = {
  setup: "setup",
  virtual_scene: "virtual_scene",
  interview: "interview",
} as const;

const SETUP_ACCORDION_IDS = {
  space: "space",
  tracking_system: "tracking_system",
  software: "software",
  audio_rendering: "audio_rendering",
} as const;

const VIRTUAL_SCENE_ACCORDION_IDS = {
  instruments: "instruments",
  black_hole: "black_hole",
} as const;

type CaseSlide =
  | "default"
  | "setup"
  | "virtual_scene"
  | "interview"
  | "space"
  | "tracking_system"
  | "software"
  | "audio_rendering"
  | "instruments"
  | "black_hole";

interface Case1ContextData {
  setActiveSlide: (slide: CaseSlide) => void;
  activeSlide: CaseSlide;
}

const Case1Context = React.createContext<Case1ContextData>({
  activeSlide: "default",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

export const Installation: React.FC<InstallationProps> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<CaseSlide>("default");

  return (
    <Case1Context.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <InstallationContent title={title} />
    </Case1Context.Provider>
  );
};

const InstallationContent: React.FC<InstallationProps> = ({ title }) => {
  const accordion = <CaseAccordion title={title} />;
  const { activeSlide } = useContext(Case1Context);

  const block = useBlocks();
  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "default":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/setup.svg`} alt={"room"} />;
      break;
    case "setup":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/setup.svg`} alt={"room"} />;
      break;
    case "space":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/space.svg`} alt={"room"} />;
      break;
    case "tracking_system":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/tracking_system.svg`} alt={"room"} />;
      break;
    case "software":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/software.svg`} alt={"room"} />;
      break;
    case "audio_rendering":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/audio_rendering.svg`} alt={"room"} />;
      break;
    case "virtual_scene":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/virtual_scene.svg`} alt={"room"} />;
      break;
    case "instruments":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/instruments.svg`} alt={"room"} />;
      break;
    case "black_hole":
      content = <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/en/black_hole.svg`} alt={"room"} />;
      break;
    case "interview":
      content = <block.video src={"https://youtu.be/CXyuQ5ANypM"} provider={"youtube"} />;
      break;
  }

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.content}>{content}</div>
    </StandardCase>
  );
};

const CaseAccordion: React.FC<InstallationProps> = () => {
  const ui = useUi();

  const { formatMessage } = useIntl();
  const [BioModalOpen, setBioModalOpen] = useState(false);

  const handleBioModalToggle = useCallback(() => setBioModalOpen((open) => !open), []);
  const block = useBlocks();

  return (
    <div className={styles.leftPanel}>
      <ui.Scrollbars>
        <div className={styles.leftPanelWrapper}>
          <div className={styles.accordion}>
            <Accordion allowMultiOpen={false} defaultOpen={[]}>
              <MainAccordionObserver />
              <AccordionItem
                id={ACCORDION_IDS.setup}
                title={formatMessage(messages.C4_P4_S1_A1_Title)}
                unmountChildrenWhenHidden
              >
                <Accordion allowMultiOpen={false} defaultOpen={[]}>
                  <SetupAccordionObserver />
                  <AccordionItem
                    id={SETUP_ACCORDION_IDS.space}
                    title={formatMessage(messages.C4_P4_S1_A2_Title)}
                    theme={"ghost"}
                  >
                    {formatMessage(messages.C4_P4_S1_A2_Description)}
                  </AccordionItem>
                  <AccordionItem
                    id={SETUP_ACCORDION_IDS.tracking_system}
                    title={formatMessage(messages.C4_P4_S1_A3_Title)}
                    theme={"ghost"}
                  >
                    {formatMessage(messages.C4_P4_S1_A3_Description)}
                  </AccordionItem>
                  <AccordionItem
                    id={SETUP_ACCORDION_IDS.software}
                    title={formatMessage(messages.C4_P4_S1_A4_Title)}
                    theme={"ghost"}
                  >
                    {formatMessage(messages.C4_P4_S1_A4_Description)}
                  </AccordionItem>
                  <AccordionItem
                    id={SETUP_ACCORDION_IDS.audio_rendering}
                    title={formatMessage(messages.C4_P4_S1_A5_Title)}
                    theme={"ghost"}
                  >
                    {formatMessage(messages.C4_P4_S1_A5_Description)}
                  </AccordionItem>
                </Accordion>
              </AccordionItem>
              <AccordionItem
                id={ACCORDION_IDS.virtual_scene}
                title={formatMessage(messages.C4_P4_S1_A6_Title)}
                unmountChildrenWhenHidden
              >
                <Accordion allowMultiOpen={false} defaultOpen={[]}>
                  <VirtualSceneAccordionObserver />
                  <AccordionItem
                    id={VIRTUAL_SCENE_ACCORDION_IDS.instruments}
                    title={formatMessage(messages.C4_P4_S1_A7_Title)}
                    theme={"ghost"}
                  >
                    {formatMessage(messages.C4_P4_S1_A7_Description)}
                  </AccordionItem>
                  <AccordionItem
                    id={VIRTUAL_SCENE_ACCORDION_IDS.black_hole}
                    title={formatMessage(messages.C4_P4_S1_A8_Title)}
                    theme={"ghost"}
                  >
                    {formatMessage(messages.C4_P4_S1_A8_Description)}
                  </AccordionItem>
                </Accordion>
              </AccordionItem>
              <AccordionItem
                id={ACCORDION_IDS.interview}
                title={formatMessage(messages.C4_P4_S1_A9_Title)}
                preventPrerender
                unmountChildrenWhenHidden
              >
                <div style={{ width: "100%", height: "142px" }}>
                  <block.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case04/daniel.png`} />
                </div>
                {formatMessage(messages.C4_P4_S1_A9_Description)}
                <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleBioModalToggle}>
                  {formatMessage(messages.C4_P4_S1_A9_BioLabel)}
                </ui.Button>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </ui.Scrollbars>
      <BioModal
        visible={BioModalOpen}
        onClose={handleBioModalToggle}
        title={formatMessage(messages.C4_P4_S1_A9_BioTitle)}
        description={formatMessage(messages.C4_P4_S1_A9_BioText)}
        linkLabel="deltasoundworks.com"
        link="http://deltasoundworks.com"
      />
    </div>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "default";
      switch (openItem) {
        case ACCORDION_IDS.setup:
          slide = "setup";
          break;
        case ACCORDION_IDS.virtual_scene:
          slide = "virtual_scene";
          break;
        case ACCORDION_IDS.interview:
          slide = "interview";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
const VirtualSceneAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "default";
      switch (openItem) {
        case VIRTUAL_SCENE_ACCORDION_IDS.instruments:
          slide = "instruments";
          break;
        case VIRTUAL_SCENE_ACCORDION_IDS.black_hole:
          slide = "black_hole";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};

const SetupAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "default";
      switch (openItem) {
        case SETUP_ACCORDION_IDS.space:
          slide = "space";
          break;
        case SETUP_ACCORDION_IDS.tracking_system:
          slide = "tracking_system";
          break;
        case SETUP_ACCORDION_IDS.software:
          slide = "software";
          break;
        case SETUP_ACCORDION_IDS.audio_rendering:
          slide = "audio_rendering";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
